import React, { createContext, useContext, useState, useEffect } from 'react'
import type { Theme, ThemeName } from './types'
import { baseTheme } from './base-theme'
import { minimalTheme } from './minimal-theme'
import { corporateTheme } from './corporate-theme'

interface ThemeContextType {
  theme: Theme
  themeName: ThemeName
  setTheme: (themeName: ThemeName) => void
  availableThemes: ThemeName[]
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

// Theme registry
const themes: Record<ThemeName, Theme> = {
  base: baseTheme,
  minimal: minimalTheme,
  corporate: corporateTheme,
  // Placeholders for future implementation
  dark: baseTheme,  // TODO: Implement dark theme
  light: baseTheme, // TODO: Implement light theme
}

interface ThemeProviderProps {
  children: React.ReactNode
  defaultTheme?: ThemeName
}

export function ThemeProvider({ children, defaultTheme = 'base' }: ThemeProviderProps) {
  const [themeName, setThemeName] = useState<ThemeName>(defaultTheme)
  
  // Load theme from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('zeigler-theme') as ThemeName
    if (savedTheme && themes[savedTheme]) {
      setThemeName(savedTheme)
    }
  }, [])
  
  // Save theme to localStorage when changed
  useEffect(() => {
    localStorage.setItem('zeigler-theme', themeName)
  }, [themeName])
  
  const setTheme = (newTheme: ThemeName) => {
    if (themes[newTheme]) {
      setThemeName(newTheme)
    }
  }
  
  const value: ThemeContextType = {
    theme: themes[themeName],
    themeName,
    setTheme,
    availableThemes: ['base', 'minimal', 'corporate'], // Only implemented themes
  }
  
  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

// Convenience hook for accessing theme tokens directly
// eslint-disable-next-line react-refresh/only-export-components
export function useThemeTokens(): Theme {
  const { theme } = useTheme()
  return theme
} 